import axios from 'axios'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

import { env } from '@/lib/env'

declare global {
  interface Window {
    Pusher: typeof Pusher
  }
}

let echoInstance: Echo<'reverb'> | null = null

/**
 * Laravel's broadcasting auth route is auto-registered by
 * `withRouting(channels: ...)` in backend/bootstrap/app.php via
 * `Broadcast::routes()`, which defaults to `/broadcasting/auth` on the
 * `web` middleware group — NOT under `/api/v1` like every other endpoint in
 * this app, and not on the `api` group either. A separate raw axios call,
 * not the shared apiClient (whose baseURL bakes in `/api/v1`), but the same
 * cookie/XSRF handling.
 */
async function authorizeChannel(
  channelName: string,
  socketId: string,
): Promise<Record<string, unknown>> {
  const { data } = await axios.post<Record<string, unknown>>(
    `${env.apiBaseUrl}/broadcasting/auth`,
    { socket_id: socketId, channel_name: channelName },
    { withCredentials: true, withXSRFToken: true, headers: { Accept: 'application/json' } },
  )
  return data
}

// pusher-js's own ChannelAuthorizerGenerator type pulls in its internal
// Channel/ChannelAuthorizationData types, which aren't worth importing here
// just to describe this one small object — cast at this single seam instead.
function buildAuthorizer(channel: { name: string }) {
  return {
    authorize(socketId: string, callback: (error: Error | null, data: unknown) => void) {
      authorizeChannel(channel.name, socketId)
        .then((data) => callback(null, data))
        .catch((err: unknown) =>
          callback(err instanceof Error ? err : new Error('Channel authorization failed'), null),
        )
    },
  }
}

/**
 * Lazily created — only call this once a session is confirmed authenticated
 * (useMeQuery succeeded), so we never open a socket / hit the auth endpoint
 * before we know who the user is.
 */
export function getEcho(): Echo<'reverb'> {
  if (echoInstance) return echoInstance

  window.Pusher = Pusher

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: env.reverbAppKey,
    wsHost: env.reverbHost,
    wsPort: env.reverbPort,
    wssPort: env.reverbPort,
    forceTLS: env.reverbScheme === 'https',
    enabledTransports: ['ws', 'wss'],
    authorizer: buildAuthorizer as unknown as ConstructorParameters<
      typeof Echo<'reverb'>
    >[0]['authorizer'],
  })

  return echoInstance
}

export function disconnectEcho(): void {
  echoInstance?.disconnect()
  echoInstance = null
}
